import { HelpCircle } from 'lucide-react';

const ACCENT = {
  blue: {
    top: 'from-transparent via-blue-500/40 to-transparent',
    iconWrap: 'bg-blue-500/15 text-blue-200 ring-blue-400/25',
  },
  amber: {
    top: 'from-transparent via-cyan-400/40 to-transparent',
    iconWrap: 'bg-cyan-500/15 text-cyan-100 ring-cyan-400/30',
  },
  cyan: {
    top: 'from-transparent via-cyan-400/40 to-transparent',
    iconWrap: 'bg-cyan-500/15 text-cyan-100 ring-cyan-400/30',
  },
  violet: {
    top: 'from-transparent via-violet-500/40 to-transparent',
    iconWrap: 'bg-violet-500/15 text-violet-200 ring-violet-400/25',
  },
  emerald: {
    top: 'from-transparent via-emerald-500/40 to-transparent',
    iconWrap: 'bg-emerald-500/15 text-emerald-200 ring-emerald-400/25',
  },
};

const ACTIVE_SHELL =
  'border-cyan-500/80 bg-cyan-950/30 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.14)] ring-1 ring-cyan-400/35 active-glow active-glow--cyan';
const IDLE_SHELL =
  'border-slate-800/80 bg-slate-900/40 text-slate-400 hover:border-slate-700/80 hover:bg-slate-900/60';

/**
 * Module selector tile — neutral idle; cyan only when ``isActive``.
 *
 * @param {{
 *   title: string,
 *   short: string,
 *   pattern?: string,
 *   help: string,
 *   Icon: import('lucide-react').LucideIcon,
 *   accent: keyof typeof ACCENT,
 *   isActive: boolean,
 *   onSelect: () => void,
 * }} props
 */
export default function HomeModuleCard({
  title,
  short,
  pattern = '',
  help,
  Icon,
  accent,
  isActive,
  onSelect,
}) {
  const a = ACCENT[accent] || ACCENT.blue;

  const onCardClick = () => onSelect();

  const onCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
      aria-pressed={isActive}
      aria-label={`${title}. Pattern: ${pattern || short}`}
      className={`home-module-card relative flex min-h-[76px] cursor-pointer flex-col justify-between gap-1 rounded-xl border p-3.5 text-left shadow-lg shadow-black/30 transition-all active:scale-[0.99] ${
        isActive ? ACTIVE_SHELL : IDLE_SHELL
      }`}
    >
      <span
        className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r opacity-90 ${a.top}`}
        aria-hidden
      />
      <section className="flex min-w-0 flex-1 items-start gap-2.5">
        <span
          className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${
            isActive ? a.iconWrap : 'bg-slate-800/60 text-slate-400 ring-slate-700/50'
          }`}
          aria-hidden
        >
          <Icon className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <section className="flex min-w-0 flex-1 flex-col justify-between">
          <section className="flex items-start justify-between gap-1">
            <h2
              className={`text-sm font-semibold leading-snug tracking-tight ${
                isActive ? 'text-cyan-100' : 'text-slate-100'
              }`}
            >
              {title}
            </h2>
            <span
              className="group/tip relative shrink-0"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-slate-500 transition hover:border-white/25 hover:text-slate-200"
                aria-label={`Technical details: ${title}`}
              >
                <HelpCircle className="h-3.5 w-3.5" aria-hidden />
              </button>
              <span
                role="tooltip"
                className="pointer-events-none invisible absolute right-0 top-full z-30 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-white/15 bg-slate-950 p-3 text-left text-xs font-mono leading-relaxed text-slate-200 opacity-0 shadow-2xl ring-1 ring-white/10 transition group-hover/tip:visible group-hover/tip:opacity-100 group-focus-within/tip:visible group-focus-within/tip:opacity-100"
              >
                {help}
              </span>
            </span>
          </section>
          {pattern ? (
            <p
              className={`home-module-pattern mt-0.5 truncate text-[11px] font-normal leading-snug tracking-normal ${
                isActive ? 'text-cyan-200' : 'text-slate-400'
              }`}
            >
              {pattern}
            </p>
          ) : (
            <p className="sr-only">{short}</p>
          )}
        </section>
      </section>
    </article>
  );
}
